import { memo, useEffect, useState } from "react"
import { motion } from "motion/react"
import { IconXmarkOutline18 as X } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { IconButton, Shortcut, Tooltip } from "@productlane/ui"

import { useRole } from "@/lib/useRole"
import { useThreadSidebarState } from "@/screens/app/sidebar"
import { useThreadContext } from "../../../thread-context"
import { useReleaseCopilotFocus } from "./copilot-focus-release"
import { ExpandToAgentButton } from "./expand-to-agent-button"
import { SidebarAiChat } from "./sidebar-ai-chat"

// Renders the AI chat as an absolutely-positioned overlay sitting on top of
// the right-bar's slot. Lives outside the thread-keyed remount boundary so
// the chat history, scroll position, in-flight stream, and entrance
// animations are preserved when the user switches threads.
//
// The right-bar (with the "details" pane) continues to occupy and reserve
// the slot inside the keyed div; this overlay just paints over it whenever
// the user has the AI Copilot tab active.
export const PersistentAiChatOverlay = memo(function PersistentAiChatOverlay() {
	const { activeTab, setActiveTab } = useThreadSidebarState()
	const { thread } = useThreadContext()
	const { role } = useRole()
	const canSeeAiChat = role !== "VIEWER"

	const isVisible = activeTab === "ai-chat" && canSeeAiChat

	// Keep SidebarAiChat unmounted until the AI chat tab is opened on this
	// thread page. Saves the autosize textarea + chat-bubble measurements that
	// otherwise run on every thread open even when the panel is hidden.
	const [hasMounted, setHasMounted] = useState(isVisible)
	useEffect(() => {
		if (isVisible) setHasMounted(true)
	}, [isVisible])

	const panelRef = useReleaseCopilotFocus(isVisible)

	if (!canSeeAiChat) return null

	return (
		<motion.div
			ref={panelRef}
			aria-hidden={!isVisible}
			initial={false}
			animate={{
				x: isVisible ? 0 : 24,
				opacity: isVisible ? 1 : 0,
			}}
			transition={{ duration: 0.24, ease: [0.23, 1, 0.32, 1] }}
			className={cn(
				"absolute right-0 top-0 z-30 flex h-full w-[var(--thread-details-width,22rem)] flex-col overflow-hidden bg-gray-50 will-change-transform dark:bg-gray-850",
				!isVisible && "pointer-events-none",
			)}
		>
			<div className="absolute right-3 top-3.5 z-30 flex items-center gap-1">
				<ExpandToAgentButton
					scope={{ kind: "thread", threadId: thread.id }}
					onExpand={() => setActiveTab("details")}
				/>
				<Tooltip
					label={
						<span className="inline-flex items-center gap-2">
							Close
							<Shortcut>
								{ShortcutSymbols.CMD}
								{ShortcutSymbols.J}
							</Shortcut>
						</span>
					}
					side="bottom"
				>
					<IconButton
						Icon={X}
						iconStrokeWidth={1.5}
						variant="ghost"
						size="default"
						aria-label="Close AI Assistant"
						onClick={() => setActiveTab("details")}
					/>
				</Tooltip>
			</div>
			{hasMounted && (
				<SidebarAiChat
					scope={{
						kind: "thread",
						threadId: thread.id,
						threadTitle: thread.title ?? undefined,
					}}
				/>
			)}
		</motion.div>
	)
})
